/**
 * Validates passenger composition for flight booking.
 * Rules:
 * 1. Total seated passengers (ADT + CHD) <= 9
 * 2. Infants (INF) <= Adults (ADT)
 * 3. Infants do not have seats.
 * 
 * @param {Object} pax - The passenger counts { adults, children, infants }
 * @returns {Object} - { valid: boolean, error: string | null, warning: string | null }
 */
export function validatePassengers(pax) {
    const { adults, children, infants } = pax;

    // Rule 1: Capacity (ADT + CHD <= 9)
    const totalSeated = adults + children;
    if (totalSeated > 9) {
        return {
            valid: false,
            error: null,
            warning: "Para grupos de más de 9 personas, por favor contacte a 'Reserva de Grupo'.",
            canAddSeated: false
        };
    }

    // Rule 2: Infants vs Adults
    if (infants > adults) {
        return {
            valid: false,
            error: "Solo se permite un infante por adulto. El infante adicional debe registrarse como Niño (CHD).",
            warning: null,
            canAddInfant: false
        };
    }

    return {
        valid: true,
        error: null,
        warning: null,
        canAddSeated: totalSeated < 9,
        canAddInfant: infants < adults
    };
}

/**
 * Helper to check if a specific addition is allowed BEFORE mutating state
 * @param {Object} currentPax 
 * @param {string} type 'adults' | 'children' | 'infants'
 * @param {number} change +1 or -1
 */
export function canUpdatePax(currentPax, type, change) {
    const nextPax = { ...currentPax, [type]: currentPax[type] + change };

    // Prevent negatives
    if (nextPax[type] < 0) return { allowed: false };
    if (type === 'adults' && nextPax.adults < 1) return { allowed: false };

    const validation = validatePassengers(nextPax);

    // If we are adding, stricter checks
    if (change > 0) {
        if (!validation.valid && validation.error) return { allowed: false, message: validation.error };
        // Special case: we allow reaching 9, but not exceeding. 
        // validatePassengers checks STATE. If state is > 9, it warns. 
        // If we are going from 9 to 10?
        if ((nextPax.adults + nextPax.children) > 9) {
            return { allowed: false, message: "Máximo 9 pasajeros (Adultos + Niños). Para grupos mayores, contáctenos." };
        }
    }

    // If removing adult matches infant count (e.g. 1 ADT 1 INF -> 0 ADT 1 INF), blocked by standard validation logic
    if (change < 0 && type === 'adults') {
        if (nextPax.infants > nextPax.adults) {
            return { allowed: false, message: "No puede reducir adultos: debe haber un adulto por cada infante." };
        }
    }

    return { allowed: true };
}
